import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  RightCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Alert, Button, Card, Collapse, Divider, Flex, Form, Modal, Radio, Space, Tag, Typography, theme } from 'antd';
import type React from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { LgBadge } from '../../../components/LgBadge';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { ParamNameTitle } from '../../../components/ParamNameTitle.tsx';
import { Price } from '../../../components/Price.tsx';
import { BillingType, type ParameterSelection_RequestableParameterFragment, Period } from '../../../graphql/generated/graphql.ts';
import type { BillingInfo } from '../../../hooks/store/useAppStore.ts';
import { useAppStore } from '../../../hooks/store/useAppStore.ts';
import { useCurrentContextStore } from '../../../hooks/store/useCurrentContextStore.ts';
import { translateBillingType } from '../../../utils/enumHelpers';
import { AVAILABLE_BILLING_INFOS_QUERY } from './SelectBillingModal.gql';

const { useToken } = theme;

const BillingEntry: React.FC<{
  title: ReactNode;
  billingInfos: BillingInfo[];
  value: BillingInfo | null;
  onChange: (billingInfo: BillingInfo) => void;
  contentProvider: (billingInfo: BillingInfo) => ReactNode;
  addTextTitle?: boolean;
}> = ({ title, billingInfos, value, onChange, contentProvider, addTextTitle }) => {
  const { token } = useToken();
  const billingInfoToValue = (billingInfo: BillingInfo): string => {
    const billingType = billingInfo.billingType;
    let id = '';
    switch (billingInfo.billingType) {
      case BillingType.SPECIAL_RATE:
        id = billingInfo.specialRateId!;
        break;
      case BillingType.DIAGNOSE:
      case BillingType.LG_DIAGNOSE:
        id = billingInfo.diagnoseId!;
        break;
    }
    return `${billingType};${id}`;
  };

  const valueToBillingInfo = (value: string): BillingInfo => {
    const [billingType, id] = value.split(';');
    const result = billingInfos.filter(it => it.billingType === billingType);
    switch (billingType) {
      case BillingType.SPECIAL_RATE:
        return result.find(it => it.specialRateId === id)!;
      case BillingType.DIAGNOSE:
      case BillingType.LG_DIAGNOSE:
        return result.find(it => it.diagnoseId === id)!;
    }
    return result[0];
  };

  return (
    <>
      <Divider
        className={css`
          margin: ${token.paddingSM}px 0;
        `}
      />
      <Form.Item
        label={
          <h4>
            {title}{' '}
            {addTextTitle && billingInfos.length && <Typography.Text type="secondary">{billingInfos[0].additionalText}</Typography.Text>}
          </h4>
        }
      >
        <Radio.Group onChange={e => onChange(valueToBillingInfo(e.target.value))} value={value ? billingInfoToValue(value) : null}>
          <Space orientation="vertical">
            {billingInfos.map((billingInfo, i) => {
              return (
                <Radio key={`${billingInfo.billingType}_${i}`} value={billingInfoToValue(billingInfo)}>
                  {contentProvider(billingInfo)}
                </Radio>
              );
            })}
          </Space>
        </Radio.Group>
      </Form.Item>
    </>
  );
};

export const SelectBillingModal: React.FC<{
  skipEnabled: boolean;
  parameter: ParameterSelection_RequestableParameterFragment | null;
  defaultSelected: BillingInfo | null;
  forceSelection: boolean;
  onCancel: () => void;
  onSkip: () => void;
  onSelect: (parameter: ParameterSelection_RequestableParameterFragment, billingInfo: BillingInfo) => void;
  flipParamNames: boolean;
  showPrices: boolean;
}> = ({ parameter, defaultSelected, onSelect, forceSelection, onCancel, onSkip, skipEnabled, flipParamNames, showPrices }) => {
  const [selectedBillingInfo, setSelectedBillingInfo] = useState<BillingInfo | null>(null);
  const { patientData, requestIdForReorder, paramQueue, aisImportResult } = useAppStore();
  const { currentDoctorId } = useCurrentContextStore();
  const { token } = theme.useToken();

  const { data, loading } = useQuery(AVAILABLE_BILLING_INFOS_QUERY, {
    variables: {
      doctorId: currentDoctorId,
      patientData: patientData!,
      requestIdForReorder: requestIdForReorder,
      catalogParamId: parameter?.id ?? '',
      selectedParameters: paramQueue.selectedParameters.map(param => ({
        parameterId: param.id,
        parameterShortName: param.shortName,
        parameterLongName: param.longName,
        billingType: param.billingInfo.billingType,
        diagnoseId: param.billingInfo.diagnoseId,
        specialRateId: param.billingInfo.specialRateId,
      })),
    },
    skip: !parameter || !!aisImportResult?.invalidPatientFields?.length,
  });

  const billingInfos = data?.availableBillingInfos?.billingInfos ?? [];
  const billingTypes = billingInfos.map(bi => bi.billingType);

  useEffect(() => {
    setSelectedBillingInfo(defaultSelected);
  }, [defaultSelected]);

  const handleCancel = () => {
    onCancel();
    setSelectedBillingInfo(null);
  };

  const handleSkip = () => {
    onSkip();
    setSelectedBillingInfo(null);
  };

  return (
    <Modal
      title={
        <span>
          <QuestionCircleOutlined />{' '}
          <ParamNameTitle shortName={parameter?.shortName ?? ''} longName={parameter?.longName ?? ''} flipParamNames={flipParamNames} />
        </span>
      }
      width={600}
      open={parameter !== null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <>
          <Divider
            className={css`
              margin-bottom: ${token.marginSM}px;
              margin-top: ${token.marginSM}px;
            `}
          />
          <Space orientation="horizontal" wrap>
            <Button hidden={forceSelection} onClick={handleCancel} icon={<CloseCircleOutlined />}>
              Abbrechen
            </Button>
            <Button hidden={forceSelection || !skipEnabled} onClick={handleSkip} icon={<RightCircleOutlined />}>
              Parameter überspringen
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={!selectedBillingInfo}
              onClick={() => {
                onSelect(parameter!, selectedBillingInfo!);
                setSelectedBillingInfo(null);
              }}
            >
              Auswählen
            </Button>
          </Space>
        </>
      }
    >
      {loading ? (
        <LoadingIndicator height="200px" />
      ) : (
        <>
          {!!data?.availableBillingInfos?.exceededPeriodicGroups.length && (
            <Space
              orientation="vertical"
              className={css`
                width: 100%;
                margin-bottom: ${token.marginSM}px;
              `}
            >
              {data?.availableBillingInfos?.exceededPeriodicGroups?.map((it, index) => {
                let periodText = '';
                switch (it.periodicGroup.period) {
                  case Period.CURRENT_REQUEST:
                    periodText = 'in dieser Anforderung (inkl. Nachforderungen)';
                    break;
                  case Period.CURRENT_DAY:
                    periodText = 'am heutigen Tag inkl. dieser Anforderung';
                    break;
                  case Period.CURRENT_QUARTER:
                    periodText = 'in diesem Quartal inkl. dieser Anforderung';
                    break;
                  case Period.CURRENT_YEAR:
                    periodText = 'in diesem Jahr inkl. dieser Anforderung';
                    break;
                }
                return (
                  <Alert
                    key={index}
                    type="warning"
                    title={`${it.lg ? 'LG-Kassenleistung' : 'Kassenleistung'} nicht verfügbar`}
                    description={
                      <Space orientation="vertical">
                        <div>
                          Dieser Parameter gehört zu einer Gruppe <strong>{it.periodicGroup.name}</strong>. Aus dieser Gruppe wurden{' '}
                          <strong>{periodText}</strong> schon <strong>{it.periodicGroup.maxCount} Parameter</strong> angefordert, alle
                          weiteren können nicht per {it.lg ? 'LG-Kassenleistung' : 'Kassenleistung'} verrechnet werden.
                        </div>
                        <Collapse
                          className={css`
                            .ant-collapse-header,
                            .ant-collapse-content-box {
                              padding: 0 !important;
                            }
                          `}
                          ghost
                          items={[
                            {
                              key: 'params',
                              label: (
                                <span
                                  className={css`
                                    color: ${token.colorLink};
                                  `}
                                >
                                  Alle Gruppen-Parameter anzeigen
                                </span>
                              ),
                              children: (
                                <Flex wrap gap="small">
                                  {[...it.paramsInGroup]
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((it, index) => (
                                      <Tag key={`${it}_${index}`}>{it}</Tag>
                                    ))}
                                </Flex>
                              ),
                            },
                          ]}
                        />
                      </Space>
                    }
                  />
                );
              })}
            </Space>
          )}
          {!!parameter?.billingDescription && (
            <Card
              style={{ marginBottom: token.marginMD, marginTop: token.marginMD }}
              type="inner"
              title="Verrechnungsinformationen"
              size="small"
            >
              <Typography.Paragraph
                className={css`
                  white-space: pre-wrap;
                  margin: 0;
                  font-size: ${token.fontSizeSM}px;
                `}
                ellipsis={{ rows: 3, expandable: true, symbol: 'Mehr anzeigen' }}
              >
                {parameter?.billingDescription}
              </Typography.Paragraph>
            </Card>
          )}
          <h4>Bitte Verrechnung für das Labor {parameter?.lab.name} auswählen:</h4>
          <Form
            layout="vertical"
            className={css`
              .ant-form-item {
                margin-bottom: 0;
              }
            `}
          >
            {billingTypes.includes(BillingType.SPECIAL_RATE) && (
              <BillingEntry
                title="Sondertarif"
                billingInfos={billingInfos.filter(bi => bi.billingType === BillingType.SPECIAL_RATE)}
                value={selectedBillingInfo}
                onChange={setSelectedBillingInfo}
                contentProvider={billingInfo => (
                  <>
                    {billingInfo.text}{' '}
                    {showPrices && (
                      <Typography.Text type="secondary">
                        <Price value={billingInfo.price} />
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            )}
            {(billingTypes.includes(BillingType.DIAGNOSE) || billingTypes.includes(BillingType.LG_DIAGNOSE)) && (
              <BillingEntry
                title="Kassenleistung mit Diagnose"
                billingInfos={
                  billingInfos.filter(bi => bi.billingType === BillingType.DIAGNOSE || bi.billingType === BillingType.LG_DIAGNOSE) ?? []
                }
                value={selectedBillingInfo}
                onChange={setSelectedBillingInfo}
                contentProvider={billingInfo => {
                  return (
                    <>
                      {billingInfo.billingType === BillingType.LG_DIAGNOSE && (
                        <>
                          <LgBadge />{' '}
                        </>
                      )}
                      {billingInfo.text}{' '}
                      {showPrices && (
                        <Typography.Text type="secondary">
                          <Price value={billingInfo.price} />
                        </Typography.Text>
                      )}
                    </>
                  );
                }}
                addTextTitle
              />
            )}
            {(billingTypes.includes(BillingType.INSURANCE) || billingTypes.includes(BillingType.LG_INSURANCE)) && (
              <BillingEntry
                title="Kassenleistung"
                billingInfos={
                  billingInfos.filter(bi => bi.billingType === BillingType.INSURANCE || bi.billingType === BillingType.LG_INSURANCE) ?? []
                }
                value={selectedBillingInfo}
                onChange={setSelectedBillingInfo}
                contentProvider={billingInfo => {
                  return (
                    <>
                      {billingInfo.billingType === BillingType.LG_INSURANCE && (
                        <>
                          <LgBadge />{' '}
                        </>
                      )}
                      {billingInfo.text}{' '}
                      {showPrices && (
                        <Typography.Text type="secondary">
                          <Price value={billingInfo.price} />
                        </Typography.Text>
                      )}
                    </>
                  );
                }}
              />
            )}
            {(billingTypes.includes(BillingType.DOCTOR) ||
              billingTypes.includes(BillingType.PATIENT) ||
              billingTypes.includes(BillingType.LG_DOCTOR)) && (
              <BillingEntry
                title="Privat"
                billingInfos={
                  billingInfos.filter(
                    bi =>
                      bi.billingType === BillingType.DOCTOR ||
                      bi.billingType === BillingType.PATIENT ||
                      bi.billingType === BillingType.LG_DOCTOR
                  ) ?? []
                }
                value={selectedBillingInfo}
                onChange={setSelectedBillingInfo}
                contentProvider={billingInfo => {
                  return (
                    <>
                      {billingInfo.billingType === BillingType.LG_DOCTOR ? (
                        <>
                          <LgBadge /> Zuweiser
                        </>
                      ) : (
                        translateBillingType(billingInfo.billingType)
                      )}{' '}
                      {showPrices && (
                        <Typography.Text type="secondary">
                          <Price value={billingInfo.price} />
                        </Typography.Text>
                      )}
                    </>
                  );
                }}
              />
            )}
            {billingTypes.includes(BillingType.DEFINE_LATER) && (
              <BillingEntry
                title={
                  <div>
                    Verrechnung später festlegen
                    <div
                      className={css`
                        font-weight: normal;
                        padding-top: 5px;
                      `}
                    >
                      <Typography.Text type="secondary">
                        <InfoCircleOutlined /> Die Anforderung kann dadurch nur vorbereitet und nicht direkt gesendet werden. Spätestens zum
                        Zeitpunkt des Sendens muss für jeden Parameter eine Auswahl getroffen werden.
                      </Typography.Text>
                    </div>
                  </div>
                }
                billingInfos={billingInfos.filter(bi => bi.billingType === BillingType.DEFINE_LATER) ?? []}
                value={selectedBillingInfo}
                onChange={setSelectedBillingInfo}
                contentProvider={billingInfo => {
                  return translateBillingType(billingInfo.billingType);
                }}
              />
            )}
          </Form>
        </>
      )}
    </Modal>
  );
};
